<template>
  <div id="app-container">
    <AppTopBar
      :username="userInfo.username || 'admin'"
      :show-admin="userInfo.rol === 'admin' || userInfo.rol === 'superadmin'"
      :show-logout="true"
      :show-vista-toggle="true"
      :vista-actual="vistaActual"
      :conteo-marketplace="conteoVistas.marketplace"
      :conteo-web="conteoVistas.web"
      @logout="logout"
      @change-vista="cambiarVista"
    />

    <div class="view-content">
      <div class="view-header">
        <span class="view-kicker">ALERTAS</span>
        <h1 class="view-title">Alertas y Oportunidades</h1>
        <span class="view-meta">Monitoreo en tiempo real de competencia</span>
      </div>

      <!-- Liderazgo + StockOut -->
      <div class="alert-grid">
        <AlertasPanel
          :alertas="alertasLiderazgo"
          nombre-cliente="Visaovip"
          :vista="vistaActual"
          @ver-producto="handleVerProducto"
        />
        <StockOutAlertas
          :alertas="alertasStockOut"
          :vista="vistaActual"
          @ver-producto="handleVerProducto"
        />
      </div>

      <MovimientoDrasticoAlertas
        :alertas="alertasMovimiento"
        :vista="vistaActual"
        @ver-producto="handleVerProducto"
      />

      <PreciosAltos
        :alertas="preciosAltosData"
        :umbral="umbralPreciosAltos"
        :vista="vistaActual"
        @umbral-change="(v) => { umbralPreciosAltos = v; loadPreciosAltos(); }"
        @verProducto="handleVerProducto"
      />

      <OportunidadesMargen
        :oportunidades="oportunidadesData"
        :umbral="umbralOportunidad"
        :vista="vistaActual"
        @umbral-change="(v) => { umbralOportunidad = v; loadOportunidades(); }"
      />

      <DiscrepanciaFuentes v-if="vistaActual === 'web'" :alertas="discrepanciaData" />

      <!-- Modal de detalle -->
      <ModalProducto
        v-model:visible="modalProductoVisible"
        :codigo="productoModalCodigo"
        :descripcion="productoModalDescripcion"
        :initial-vista="vistaActual"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

import AppTopBar from '../components/AppTopBar.vue';
import AlertasPanel from '../components/AlertasPanel.vue';
import StockOutAlertas from '../components/StockOutAlertas.vue';
import MovimientoDrasticoAlertas from '../components/MovimientoDrasticoAlertas.vue';
import PreciosAltos from '../components/PreciosAltos.vue';
import OportunidadesMargen from '../components/OportunidadesMargen.vue';
import DiscrepanciaFuentes from '../components/DiscrepanciaFuentes.vue';
import ModalProducto from '../components/ModalProducto.vue';

const router = useRouter();
const API_BASE_URL = 'https://dash-visaovip.kompara.tech/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('token_precios');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const userInfo = ref({ username: '', rol: '' });
const vistaActual = ref(localStorage.getItem('vista_actual') || 'web');
const conteoVistas = ref({ web: 0, marketplace: 0 });

const alertasLiderazgo = ref([]);
const alertasStockOut = ref([]);
const alertasMovimiento = ref([]);
const preciosAltosData = ref([]);
const oportunidadesData = ref([]);
const discrepanciaData = ref([]);

const umbralPreciosAltos = ref(10);
const umbralOportunidad = ref(10);

const modalProductoVisible = ref(false);
const productoModalCodigo = ref('');
const productoModalDescripcion = ref('');

const cambiarVista = (v) => {
  vistaActual.value = v;
  localStorage.setItem('vista_actual', v);
};

const logout = () => {
  localStorage.removeItem('token_precios');
  router.push('/');
  window.location.reload();
};

const handleVerProducto = ({ codigo, descripcion }) => {
  productoModalCodigo.value = String(codigo);
  productoModalDescripcion.value = descripcion || '';
  modalProductoVisible.value = true;
};

const loadAlertasLiderazgo = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/alertas/liderazgo-perdido`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value }
    });
    alertasLiderazgo.value = res.data;
  } catch (e) { console.error(e); }
};

const loadStockOut = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/alertas/stock-out-competidor`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value }
    });
    alertasStockOut.value = res.data;
  } catch (e) { console.error(e); }
};

const loadMovimiento = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/alertas/movimiento-drastico`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value }
    });
    alertasMovimiento.value = res.data;
  } catch (e) { console.error(e); }
};

const loadPreciosAltos = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/estrategia/precios-altos`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value, umbral: umbralPreciosAltos.value }
    });
    preciosAltosData.value = res.data;
  } catch (e) { console.error(e); }
};

const loadOportunidades = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/estrategia/oportunidades-margen`, {
      ...getAuthHeaders(), params: { vista: vistaActual.value, umbral: umbralOportunidad.value }
    });
    oportunidadesData.value = res.data;
  } catch (e) { console.error(e); }
};

const loadDiscrepancia = async () => {
  if (vistaActual.value !== 'web') {
    discrepanciaData.value = [];
    return;
  }
  try {
    const res = await axios.get(`${API_BASE_URL}/alertas/discrepancia-fuentes`, getAuthHeaders());
    discrepanciaData.value = res.data;
  } catch (e) { console.error(e); }
};

const loadConteo = async () => {
  for (const v of ['web', 'marketplace']) {
    try {
      const res = await axios.get(`${API_BASE_URL}/kpis/overview`, {
        ...getAuthHeaders(), params: { vista: v }
      });
      conteoVistas.value[v] = res.data.competitive_products_count || 0;
    } catch (e) { /* ignore */ }
  }
};

const loadAll = async () => {
  await Promise.all([
    loadAlertasLiderazgo(), loadStockOut(), loadMovimiento(),
    loadPreciosAltos(), loadOportunidades(), loadDiscrepancia()
  ]);
};

watch(vistaActual, loadAll);

onMounted(async () => {
  const token = localStorage.getItem('token_precios');
  if (!token) { router.push('/'); return; }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    userInfo.value = { username: payload.sub || 'admin', rol: payload.rol || 'admin' };
  } catch { /* ignore */ }
  await loadAll();
  loadConteo();
});
</script>

<style scoped>
#app-container { min-height: 100vh; background: var(--color-background); color: var(--color-text); }
.view-content {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 1800px;
  margin: 0 auto;
}
.view-header {
  padding: 4px 0 14px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 6px;
}
.view-kicker {
  display: block;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.view-title {
  margin: 4px 0 4px;
  font-family: 'Inter', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.01em;
}
.view-meta {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  letter-spacing: 0.03em;
}
.alert-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
@media (max-width: 1000px) {
  .alert-grid { grid-template-columns: 1fr; }
}
</style>